<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        md="4"
      >
        <VTextField
          v-model="data.jobName"
          :label="$t('forms.labels.jobName')"
          :rules="{ max: 200 }"
          dense
          outlined
          :disabled="!hasPermission('workOrders.edit') || isCompletedOrLocked"
          :readonly="!isEdition"
          :dataCy="dataCy.workOrders.workOrderView.inputs.jobName"
          counter
          @input="debouncedInput('jobName')"
        />
        <VTextField
          v-model="data.client"
          :label="$t('forms.labels.client')"
          dense
          outlined
          :readonly="true"
          :dataCy="dataCy.workOrders.workOrderView.inputs.client"
        />
        <VTextField
          v-model="data.site"
          dense
          outlined
          :label="$t('forms.labels.siteName')"
          :readonly="true"
          :dataCy="dataCy.workOrders.workOrderView.inputs.siteName"
        />
      </v-col>
      <v-col
        cols="12"
        md="4"
      >
        <v-row>
          <v-col class="pt-0 pb-0">
            <VTextField
              v-model="data.poNumber"
              dense
              outlined
              :label="$t('forms.labels.poNumber')"
              :rules="{ required: false, max: 200 }"
              :disabled="!hasPermission('workOrders.edit') || isCompletedOrLocked"
              :readonly="!isEdition"
              :dataCy="dataCy.workOrders.workOrderView.inputs.poNumber"
              counter
              @input="debouncedInput('poNumber')"
            />
          </v-col>
          <v-col class="pt-0 pb-0">
            <VTextField
              v-model="data.soNumber"
              dense
              outlined
              :label="$t('forms.labels.soNumber')"
              :rules="{ required: false, max: 200 }"
              :disabled="!hasPermission('workOrders.edit') || isCompletedOrLocked"
              :readonly="!isEdition"
              :dataCy="dataCy.workOrders.workOrderView.inputs.soNumber"
              counter
              @input="debouncedInput('soNumber')"
            />
          </v-col>
        </v-row>
        <VDatePicker
          v-model="data.startDate"
          dense
          :label="$t('forms.labels.startDate')"
          :disabled="!hasPermission('workOrders.edit') || isCompletedOrLocked || !isEdition"
          :locale="locale"
          :dataCy="dataCy.workOrders.workOrderView.inputs.startDate"
          @input="debouncedInput('startDate')"
        />
        <VDatePicker
          v-model="data.endDate"
          dense
          :label="$t('forms.labels.endDate')"
          :disabled="!hasPermission('workOrders.edit') || isCompletedOrLocked || !isEdition"
          :locale="locale"
          :dataCy="dataCy.workOrders.workOrderView.inputs.endDate"
          @input="debouncedInput('endDate')"
        />
      </v-col>
      <v-col
        cols="12"
        md="4"
      >
        <VTextarea
          v-model="data.description"
          autoGrow
          outlined
          rows="4"
          :label="$t('forms.labels.description')"
          :rules="{ required: false, max: 1000 }"
          :disabled="!hasPermission('workOrders.edit') || isCompletedOrLocked"
          :readonly="!isEdition"
          :dataCy="dataCy.workOrders.workOrderView.inputs.description"
          @input="debouncedInput('description')"
        />
        <v-checkbox
          v-model="data.billable"
          dense
          color="primary"
          :label="$t('forms.labels.billable')"
          :disabled="!hasPermission('workOrders.edit') || isCompletedOrLocked"
          :readonly="!isEdition"
          :dataCy="dataCy.workOrders.workOrderView.inputs.billable"
          style="margin-top: 0px"
          @change="debouncedInput('billable')"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import validationProviderHelperMixin from '@/components/shared/widgets/forms/validationHelper/validationProviderHelperMixin';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField';
import VDatePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDatePicker';
import workOrderStatusChoices from '@/constants/workOrderStatusChoices';
import _datePickerLocales from '@/locales/date-picker-locales';
import { workOrders } from '@/aq-data-cy.json';


export default {
    components: {
        VTextarea,
        VTextField,
        VDatePicker,
    },
    mixins: [
        validationProviderHelperMixin,
    ],
    props: {
        workOrder: {
            type: Object,
            default: null,
        },
        isCompletedOrLocked: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            clients: [],
            sites: [],
            isEdition: true,
            debouncedInput: debounce(this.onInput, 500),
            data: {
                contractNumber: '',
                jobName: '',
                client: '',
                site: '',
                poNumber: 0,
                soNumber: 0,
                startDate: null,
                endDate: null,
                billable: false,
                description: '',
                completionStatus: '',
            },
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'user',
            'hasRole',
        ]),
        locale() {
            return _datePickerLocales[this.lang];
        },
        completionStatus() {
            return Object.values(workOrderStatusChoices).map(status => ({
                name: this.$t(`forms.labels.${status}`),
                isDisabled: status === 'completed',
                key: status,
            }));
        },
        dataCy() {
            return {
                workOrders,
            };
        },
    },
    watch: {
        async $route() {
            this.setData();
        },
    },
    mounted() {
        this.setData();
    },
    methods: {
        ...mapActions({
            reloadWorkOrder: 'getWorkOrderInfo',
        }),
        async getMembers() {
            const idClient = this.workOrder?.site?.client?.ref;
            let response;
            if (idClient) {
                response = await this.$rq.getClientMembers(idClient);
                if (response && response.data) {
                    const { data } = response;
                    this.members = data;
                }
            }
        },
        convertDatesToDateTime(data, key) {
            // startDate and endDate must be converted to ISO 8601
            // before posting to keep user timezone
            if (key === 'startDate') {
                return moment(data).startOf('day');
            } if (key === 'endDate') {
                return moment(data).endOf('day');
            }
            return data;
        },

        setData() {
            if (this.$route.params.isEdition) {
                this.isEdition = true;
            }
            this.getMembers();
            this.data.contractNumber = this.workOrder.contractNumber;
            this.data.jobName = this.workOrder.jobName;
            this.data.client = this.workOrder?.site?.client?.name;
            this.data.site = this?.workOrder?.site?.name;
            this.data.poNumber = this.workOrder.poNumber;
            this.data.soNumber = this.workOrder.soNumber;
            this.data.startDate = this.workOrder.startDate;
            this.data.endDate = this.workOrder.endDate || null;
            this.data.billable = this.workOrder.billable;
            this.data.description = this.workOrder.description;
            this.data.completionStatus = this.workOrder.completionStatus;
        },
        async onInput(key) {
            const value = this.convertDatesToDateTime(this.data[key], key);
            const { id } = this.workOrder;
            // Update data
            await this.$rq.updateWorkOrder(id, { [key]: value });
            await this.reloadWorkOrder(id);
        },
    },
};
</script>

<style lang="scss" scoped>
  @import "@/assets/styles/utils/_variables.scss";

  .wo-number-tag {
    display: inline-block;
    background-color: $primary-color;
    color: #fff;
    padding: 3px 8px;
  }
  .subtitle-2 {
    margin-bottom: 0.8rem;
    color: $primary-color;
  }
  ::v-deep .wo-number{
  background-color: #26384B;
  color: white;
  width: fit-content;
  font-size: 20px;
  font-weight: bold;
}
</style>
